import { Link, Stack, Typography } from "@mui/material"
import { ArrowNarrowDown, Share05 } from "@untitled-ui/icons-react"

import { TokenInfo } from "../../ao/token"
import { NumberFont, TitleFont } from "../../theme"
import { parseBigIntAsNumber, formatTicker, formatNumberAuto } from "../../utils/format"
import { SuccessIcon } from "../SuccessIcon"
import TokenLogo from "../TokenLogo"
import { TokenPill } from "../TokenPill"

type RemoveOrderSuccessFormProps = {
  lpAmountNormalized: bigint
  lpToken: TokenInfo
  tokenA: TokenInfo
  tokenB: TokenInfo
  tokenAOutput: bigint
  tokenBOutput: bigint
  poolId: string
  orderConfirmationId: string
}

export function RemoveOrderSuccessForm(props: RemoveOrderSuccessFormProps) {
  const {
    poolId,
    orderConfirmationId,
    lpAmountNormalized,
    tokenAOutput,
    tokenBOutput,
    lpToken,
    tokenA,
    tokenB,
  } = props

  if (lpAmountNormalized === 0n) {
    return
  }

  return (
    <>
      <Stack gap={1} alignItems="center">
        <SuccessIcon />
        <Typography variant="h5" fontFamily={TitleFont}>
          Successful Withdrawal
        </Typography>
        <Stack direction={"row"} gap={1}>
          Withdrew:{" "}
          <TokenPill variant="text">
            {formatNumberAuto(parseBigIntAsNumber(lpAmountNormalized, lpToken.denomination))}{" "}
            <TokenLogo tx={lpToken.logo} alt={`${lpToken.name} logo`} size="small" />
            {formatTicker(lpToken.ticker)}
          </TokenPill>
        </Stack>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            width: "100%",
            "& > *": {
              flex: 1,
              flexShrink: 0,
              overflow: "hidden",
            },
            "& > *:nth-child(1)": {
              flexBasis: "calc(50% - 12px - var(--spacing))",
              alignItems: "flex-end",
            },
            "& > *:nth-child(2)": {
              flexBasis: "24px",
            },
            "& > *:nth-child(3)": {
              flexBasis: "calc(50% - 12px - var(--spacing))",
            },
          }}
        >
          <Stack>
            <TokenPill variant="outlined">
              <TokenLogo tx={tokenA.logo} alt={`${tokenA.name} logo`} size="small" />
              <Typography
                fontFamily={NumberFont}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {parseBigIntAsNumber(tokenAOutput, tokenA.denomination, tokenA.denomination)}{" "}
              </Typography>
              {formatTicker(tokenA.ticker)}
            </TokenPill>
          </Stack>
          <ArrowNarrowDown />
          <Stack>
            <TokenPill variant="outlined">
              <TokenLogo tx={tokenB.logo} alt={`${tokenB.name} logo`} size="small" />
              <Typography
                fontFamily={NumberFont}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {parseBigIntAsNumber(tokenBOutput, tokenB.denomination, tokenB.denomination)}{" "}
              </Typography>
              {formatTicker(tokenB.ticker)}
            </TokenPill>
          </Stack>
        </Stack>
        <Link
          href={`https://ao.link/#/message/${orderConfirmationId}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            marginTop: 2,
            gap: 0.5,
            display: "flex",
            alignItems: "center",
            flexDirection: "row",

            "& svg": {
              width: "1rem",
              height: "1rem",
            },
          }}
          color="secondary"
        >
          View on ao.link
          <Share05 />
        </Link>
        <Link
          href={`https://dexi.arweave.net/#/pool/${poolId}?burnId=${orderConfirmationId}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            marginBottom: 2,
            gap: 0.5,
            display: "flex",
            alignItems: "center",
            flexDirection: "row",

            "& svg": {
              width: "1rem",
              height: "1rem",
            },
          }}
          color="secondary"
        >
          View on Dexi
          <Share05 />
        </Link>
      </Stack>
    </>
  )
}
